import axios, { AxiosError, AxiosProgressEvent, AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { clearAppStorage } from "../utils/authStorage";
import { ServerStep, trackRequest } from "./requestSteps";

// const BASE_URL = "https://api.promarketer.ai/";
// const BASE_URL = "https://amsapi.growvanaus.com/"; //<-- New Amazon API URL
// const BASE_URL = "https://demoapi.webmaxy.co/"; //<-- New Walmart API URL
const BASE_URL = process.env.REACT_APP_BASE_URL;
// const BASE_URL = "http://127.0.0.1:9100/";

interface PropData {
  action: string;
  body?: any;
  isFormData?: boolean;
  isShowError?: boolean;
  id?: string;
  _file?: File;
  key?: string;
  signal?: any
}

// ─── Server-reported steps ────────────────────────────────────────────────────
// A slow list GET can answer as NDJSON: the steps it is on, one JSON object per
// line, then {"type":"result","status":…,"body"|"text":…} last. The format is
// backend Utility/Progress.js. A quick answer is plain JSON, as it always was.
const NDJSON = "application/x-ndjson";

const isStreamed = (contentType: unknown) => String(contentType ?? "").includes(NDJSON);

const STEP_STATES = new Set(["active", "done", "failed"]);
const isServerStep = (value: any): value is ServerStep =>
  !!value && typeof value.id === "string" && typeof value.label === "string" && STEP_STATES.has(value.state);

// Hands each step line to `onStep` as it arrives: axios reports the download's
// progress, and the XHR's responseText is everything received so far.
const readStepLines = (onStep: (step: ServerStep) => void) => {
  let read = 0;
  return (progress: AxiosProgressEvent) => {
    const xhr = progress.event?.target as XMLHttpRequest | undefined;
    if (!xhr || !isStreamed(xhr.getResponseHeader("Content-Type"))) return;
    const text = xhr.responseText;
    for (let end = text.indexOf("\n", read); end !== -1; end = text.indexOf("\n", read)) {
      const line = text.slice(read, end);
      read = end + 1;
      // The server writes "type" first. The result line, which can be
      // megabytes, is left for the end.
      if (!line.startsWith('{"type":"step"')) continue;
      try {
        const step = JSON.parse(line);
        if (isServerStep(step)) onStep(step);
      } catch {
        // A line that does not parse is skipped; the next one replaces it.
      }
    }
  };
};

// What axios does with a plain body: JSON when it parses, else the text.
const parseText = (text: unknown) => {
  if (typeof text !== "string" || text === "") return text ?? "";
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

// The handler's answer. A streamed one carries the handler's own status in its
// result line (the HTTP status is 200 once streaming starts), so 400 and up
// becomes the same error axios would have thrown for a plain answer.
const unwrapResult = (response: AxiosResponse) => {
  if (!isStreamed(response.headers["content-type"])) return response.data;
  let result: any = null;
  if (typeof response.data === "string") {
    const body = response.data.trimEnd();
    try {
      result = JSON.parse(body.slice(body.lastIndexOf("\n") + 1));
    } catch {
      result = null;
    }
  } else {
    // A body of one line was already parsed by axios.
    result = response.data;
  }
  if (!result || result.type !== "result") {
    throw new AxiosError(
      "The server's answer ended before its result.",
      AxiosError.ERR_BAD_RESPONSE,
      response.config,
      response.request
    );
  }
  const data = "body" in result ? result.body : parseText(result.text);
  const status = Number(result.status) || 200;
  if (status >= 400) {
    throw new AxiosError(
      `Request failed with status code ${status}`,
      status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
      response.config,
      response.request,
      { ...response, status, statusText: "", data }
    );
  }
  return data;
};

const WebServiceAmazon = {
  getBaseUrl: function () {
    return BASE_URL;
  },
  postAPI: function (props: PropData) {
    return new Promise((resolve, reject) => {
      var bodyFormData = new URLSearchParams();
      for (let key in props.body) {
        bodyFormData.append(key, props.body[key]);
      }
      const headers = {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("access_token"),
        appid: localStorage.getItem("app_id"),
        xapikey: localStorage.getItem("p_user_id"),
        'x-tenant-id': localStorage.getItem("t_id"),
      };
      axios
        .post(
          `${BASE_URL}${props.action}`,
          props.isFormData ? bodyFormData : props.body,
          {
            headers: headers,
          }
        )
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          if (error && error.response && error.response.status === 401) {
            this.logout();
          }

          props.isShowError ? reject(this.errorHandler(error)) : reject(error);
        });
    });
  },
  uploadAPI: function (props: PropData) {
    let formData = new FormData();
    if (props._file && props.key) {
      formData.append(props.key, props._file);
    }
    return new Promise((resolve, reject) => {
      for (let key in props.body) {
        formData.append(key, props.body[key]);
      }
      const headers = {
        Authorization: "Bearer " + localStorage.getItem("access_token"),
        appid: localStorage.getItem("app_id"),
        'x-tenant-id': localStorage.getItem("t_id"),
      };
      axios
        .post(`${BASE_URL}${props.action}`, formData, {
          headers: headers,
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          if (error && error.response && error.response.status === 401) {
            this.logout();
          }

          props.isShowError ? reject(this.errorHandler(error)) : reject(error);
        });
    });
  },
  putAPI: function (props: PropData) {
    return new Promise((resolve, reject) => {
      var bodyFormData = new URLSearchParams();
      for (let key in props.body) {
        bodyFormData.append(key, props.body[key]);
      }
      const headers = {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("access_token"),
        appid: localStorage.getItem("app_id"),
        xapikey: localStorage.getItem("p_user_id"),
        'x-tenant-id': localStorage.getItem("t_id"),
      };
      axios
        .put(`${BASE_URL}${props.action}`, props.body, {
          headers: headers,
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          if (error && error.response && error.response.status === 401) {
            this.logout();
          }
          props.isShowError ? reject(this.errorHandler(error)) : reject(error);
        });
    });
  },

  getAPI: function (props: PropData) {
    // Tells the step loader this request is in flight; a no-op for a path it
    // has no label for.
    const tracker = trackRequest(props.action);
    // A request the loader draws also asks the server for the steps it is on.
    // Never an export: that is a file download.
    const withSteps = tracker.tracked && !/[?&]isExport=/.test(props.action);
    return new Promise((resolve, reject) => {
      const headers = {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: "Bearer " + localStorage.getItem("access_token"),
        appid: localStorage.getItem("app_id"),
        'x-tenant-id': localStorage.getItem("t_id"),
        ...(withSteps && { "X-Progress": "1" }),
      };
      var ad: any = {
        headers: headers,
      };
      if (props.signal) {
        ad = {
          headers: headers,
          signal: props.signal
        }
      }
      if (withSteps) ad.onDownloadProgress = readStepLines(tracker.serverStep);
      axios
        .get(`${BASE_URL}${props.action}`, ad)
        .then((response) => (withSteps ? unwrapResult(response) : response.data))
        .then((data) => {
          tracker.settle("done");
          resolve(data);
        })
        .catch((error) => {
          tracker.settle(axios.isCancel(error) ? "aborted" : "failed");
          if (error && error.response && error.response.status === 401) {
            this.logout();
          }

          props.isShowError ? reject(this.errorHandler(error)) : reject(error);
        });
    });
  },

  deleteAPI: function (props: PropData) {
    return new Promise((resolve, reject) => {
      const headers = {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: "Bearer " + localStorage.getItem("access_token"),
        appid: localStorage.getItem("app_id"),
        'x-tenant-id': localStorage.getItem("t_id"),
      };
      axios
        .delete(`${BASE_URL}${props.action}`, {
          headers: headers,
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          if (error && error.response && error.response.status === 401) {
            this.logout();
          }

          props.isShowError ? reject(this.errorHandler(error)) : reject(error);
        });
    });
  },

  errorHandler: function (error: any) {
    if (error?.response) {
      error = error.response;
    }
    var errorMessage;
    if (!error || !error.status) {
      errorMessage = "Server Not Responding";
    } else if (error.status === 401) {
      errorMessage = "Unauthorized";
      this.logout();
    } else if (error.status === 500) {
      errorMessage =
        (error &&
          error.data &&
          error.data.ErrorDetails &&
          error.data.ErrorDetails.message) ||
        "An unkown exception has occur. Please Contact administrator";
    } else if (error.status === 503) {
      return errorMessage;
    } else {
      errorMessage = error.data.message;
    }
    toast.error(errorMessage);
    return errorMessage;
  },

  addLoader(id: any) {
    if (id) {
      var button = document.getElementById(id) as HTMLButtonElement | null;
      if (button != null) {
        button.disabled = true;
        var loader = document.createElement("span");
        loader.className = "loader";
        button.prepend(loader);
      }
    }
  },

  removeLoader(id: any) {
    if (id) {
      var button = document.getElementById(id) as HTMLButtonElement | null;
      if (button != null) {
        button.disabled = false;
        button.removeChild(button.childNodes[0]);
      }
    }
  },
  getBaseURL() {
    return BASE_URL;
  },

  logout() {
    const url = `${window.location.protocol}//${window.location.host}/login`;
    clearAppStorage();
    window.location.assign(url);
  }
};

export default WebServiceAmazon;
